import { AlertTriangle, Lock, Printer, Truck } from 'lucide-react'

import type { CarrierTone } from '@/lib/carrier-status'

import { CarrierCancelAction } from '@/components/carrier/carrier-cancel-action'
import { CarrierReturnActions } from '@/components/carrier/carrier-return-actions'
import { CarrierTrackingTimeline } from '@/components/carrier/carrier-tracking-timeline'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import {
    useCarrierLabel,
    useCarrierShipment,
} from '@/hooks/use-carrier-shipment-query'
import { useAuth } from '@/hooks/use-auth'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import {
    carrierShipmentDisplay,
    isCarrierReturnRequested,
} from '@/lib/carrier-status'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

type BadgeVariant =
    | 'default'
    | 'secondary'
    | 'destructive'
    | 'outline'
    | 'success'
    | 'warning'

const TONE_VARIANTS: Record<CarrierTone, BadgeVariant> = {
    neutral: 'secondary',
    progress: 'default',
    success: 'success',
    warning: 'warning',
    danger: 'destructive',
}

const TERMINAL_CARRIER_CODES = new Set([
    '101',
    '107',
    '201',
    '501',
    '503',
    '504',
])

interface Props {
    orderId: string
}

export function CarrierTrackingPanel({ orderId }: Props) {
    const enabled = Boolean(useFeatureFlag('dev-carrier-shipping'))
    const { hasPermission } = useAuth()
    const query = useCarrierShipment(orderId)
    const label = useCarrierLabel(orderId)

    if (query.isLoading) {
        return (
            <div className="text-muted-foreground rounded-md border p-3 text-sm">
                {m.carrier_tracking_loading()}
            </div>
        )
    }
    if (query.isError) {
        return (
            <div
                className="border-destructive/30 bg-destructive/5 flex items-center justify-between gap-3 rounded-md border p-3 text-sm"
                role="alert"
            >
                <span>{m.carrier_tracking_error()}</span>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void query.refetch()}
                >
                    {m.carrier_tracking_retry()}
                </Button>
            </div>
        )
    }
    const shipment = query.data
    if (!shipment) return null

    const display = carrierShipmentDisplay(shipment)
    const canCancel =
        enabled &&
        hasPermission('carriers.dispatch') &&
        !TERMINAL_CARRIER_CODES.has(shipment.carrier_status_code ?? '') &&
        (shipment.status === 'pending' || shipment.status === 'created')

    const printLabel = async () => {
        try {
            const url = await label.mutateAsync()
            window.open(url, '_blank', 'noopener,noreferrer')
        } catch {
            // The label hook owns the operator-visible error.
        }
    }

    return (
        <div className="space-y-3 rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-2">
                <Truck className="text-muted-foreground size-4" />
                <span className="text-sm font-medium">
                    {m.carrier_panel_title()}
                </span>
                <Badge variant="outline">
                    {m.carrier_carrier_name_viettelpost()}
                </Badge>
                <Badge variant={TONE_VARIANTS[display.tone]}>
                    {display.label}
                </Badge>
            </div>

            {isCarrierReturnRequested(shipment) && (
                <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-2">
                    <div className="text-destructive flex items-start gap-2 text-sm">
                        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                        <span>{m.carrier_return_requested()}</span>
                    </div>
                    <CarrierReturnActions
                        orderId={orderId}
                        shipment={shipment}
                        canResolve={
                            enabled && hasPermission('carriers.dispatch')
                        }
                    />
                </div>
            )}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <dt className="text-muted-foreground">
                    {m.carrier_tracking_code()}
                </dt>
                <dd className="font-medium">
                    {shipment.tracking_code ?? (
                        <span className="text-muted-foreground font-normal">
                            {m.carrier_awaiting_waybill()}
                        </span>
                    )}
                </dd>
                {shipment.service_code && (
                    <>
                        <dt className="text-muted-foreground">
                            {m.carrier_service()}
                        </dt>
                        <dd>{shipment.service_code}</dd>
                    </>
                )}
                <dt className="text-muted-foreground">
                    {m.carrier_shipping_fee()}
                </dt>
                <dd>{formatVnd(shipment.shipping_fee)}</dd>
                {shipment.cod_amount > 0 && (
                    <>
                        <dt className="text-muted-foreground">
                            {m.carrier_cod_amount()}
                        </dt>
                        <dd>{formatVnd(shipment.cod_amount)}</dd>
                    </>
                )}
                {shipment.legacy_ward_name && (
                    <dd className="col-span-2 flex items-center gap-1.5">
                        <span>
                            {m.carrier_legacy_locked({
                                ward: shipment.legacy_ward_name,
                                district: shipment.legacy_district_name ?? '',
                            })}
                        </span>
                        {canCancel && (
                            <Tooltip content={m.carrier_legacy_locked_hint()}>
                                <span
                                    tabIndex={0}
                                    role="img"
                                    aria-label={m.carrier_legacy_locked_hint()}
                                    className="text-muted-foreground inline-flex"
                                >
                                    <Lock className="size-3.5" />
                                </span>
                            </Tooltip>
                        )}
                    </dd>
                )}
                {shipment.allow_inspection !== undefined && (
                    <>
                        <dt className="text-muted-foreground">
                            {m.carrier_allow_inspection()}
                        </dt>
                        <dd>
                            {shipment.allow_inspection
                                ? m.carrier_inspection_with_fee({
                                      fee: formatVnd(
                                          shipment.inspection_fee_vnd,
                                      ),
                                  })
                                : m.common_no()}
                        </dd>
                    </>
                )}
            </dl>

            <div className="flex flex-wrap gap-2">
                {enabled &&
                    shipment.tracking_code &&
                    hasPermission('carriers.label') && (
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={label.isPending}
                            onClick={() => void printLabel()}
                        >
                            <Printer className="size-3.5" />
                            {m.carrier_print_label()}
                        </Button>
                    )}
                {canCancel && <CarrierCancelAction orderId={orderId} />}
            </div>

            <CarrierTrackingTimeline events={shipment.events ?? []} />
        </div>
    )
}
